import { useAuth, useUser } from '@clerk/clerk-react'
import { Configuration } from '@saaga/billingv2-sdk'
import { useCallback, useMemo } from 'react'

const BILLING_API_URL = import.meta.env.VITE_GATEWAY_BILLING
const BILLING_TIMEOUT_MS = 30000

const fetchWithTimeout = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), BILLING_TIMEOUT_MS)

  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Billing request timed out')
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

export interface BillingUserInfo {
  name: string
  email: string
}

export interface UseBillingConfigReturn {
  createConfig: (includeTenantId?: boolean) => Promise<Configuration>
  userInfo: BillingUserInfo
  orgId: string | null | undefined
}

export const useBillingConfig = (): UseBillingConfigReturn => {
  const { getToken, orgId } = useAuth()
  const { user } = useUser()

  const createConfig = useCallback(
    async (includeTenantId = true): Promise<Configuration> => {
      const token = await getToken()

      if (!token) {
        throw new Error('No authentication token available')
      }

      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      }

      if (includeTenantId) {
        if (!orgId) {
          throw new Error('No organization selected')
        }
        headers['x-tenant-id'] = orgId
      }

      return new Configuration({
        basePath: BILLING_API_URL,
        headers,
        fetchApi: fetchWithTimeout,
      })
    },
    [getToken, orgId]
  )

  const userInfo = useMemo<BillingUserInfo>(
    () => ({
      name: user?.fullName ?? user?.firstName ?? 'User',
      email: user?.primaryEmailAddress?.emailAddress ?? '',
    }),
    [user?.fullName, user?.firstName, user?.primaryEmailAddress?.emailAddress]
  )

  return { createConfig, userInfo, orgId }
}
